import { cn } from "cn";
import { ChevronDown } from "lucide-react";
import type * as React from "react";

interface ListSectionHeaderProps {
  title: React.ReactNode;
  /** Shown after the title ("Pre-chart 3"). */
  count?: number;
  /** A leading icon (the pre-chart clipboard). */
  icon?: React.ReactNode;
  /** With onToggle the header is a button that folds the section. */
  expanded?: boolean;
  onToggle?: () => void;
  /** The id of the list the header folds (aria-controls). */
  controls?: string;
  /**
   * "sidebar": the desktop sidebar; "page": the phone visits screen;
   * "home": the All visits page. Same spacing as their date labels.
   */
  size?: "sidebar" | "page" | "home";
  className?: string;
  id?: string;
  testId?: string;
}

// A group label in the v3.2 visits list (Today, Yesterday, Pre-chart), with
// an optional count and, when foldable, a chevron that turns as the section
// opens and closes. The whole label is the button (44px tap area on phones).
export function ListSectionHeader({
  title,
  count,
  icon,
  expanded = true,
  onToggle,
  controls,
  size = "sidebar",
  className,
  id,
  testId,
}: ListSectionHeaderProps): React.JSX.Element {
  const label = (
    <>
      {icon && (
        <span
          aria-hidden
          className="inline-flex size-3.5 shrink-0 [&>svg]:size-3.5"
        >
          {icon}
        </span>
      )}
      <span className="truncate">{title}</span>
      {count !== undefined && (
        <span
          className="rounded-full bg-v3-surface-3 px-1.5 text-[11.5px] font-semibold leading-[18px] tabular-nums text-v3-text-2 dark:bg-v3-surface-2"
          data-testid={testId ? `${testId}-count` : undefined}
        >
          {count}
        </span>
      )}
    </>
  );
  const text = cn(
    "flex min-w-0 items-center gap-1.5 font-v3 font-medium text-v3-text-3",
    size === "page"
      ? "text-[13.5px]"
      : size === "home"
        ? "text-[12.5px]"
        : "text-[12px]",
  );
  return (
    <h2
      className={cn(
        size === "page"
          ? "px-1 pb-2 pt-5"
          : size === "home"
            ? "px-1 pb-2"
            : "px-2.5 pb-1 pt-4",
        className,
      )}
      id={id}
    >
      {onToggle ? (
        <button
          aria-controls={controls}
          aria-expanded={expanded}
          className={cn(
            text,
            "-mx-1.5 min-h-11 w-[calc(100%+0.75rem)] rounded-lg px-1.5 text-left transition-colors hover:text-v3-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring lg:min-h-7",
          )}
          data-testid={testId}
          type="button"
          onClick={onToggle}
        >
          {label}
          <ChevronDown
            aria-hidden
            className={cn(
              "ml-auto size-4 shrink-0 transition-transform duration-150 motion-reduce:transition-none",
              !expanded && "-rotate-90",
            )}
          />
        </button>
      ) : (
        <span className={text} data-testid={testId}>
          {label}
        </span>
      )}
    </h2>
  );
}

interface ListSectionMoreProps {
  /** "Show all 44" while trimmed, "Show less" while showing all. */
  label: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  /** The id of the list it grows (aria-controls). */
  controls?: string;
  /** "sidebar": a sidebar row; "card": a row of a list card. */
  variant?: "sidebar" | "card";
  className?: string;
  testId?: string;
}

// The last row of a trimmed section ("Show all 44" / "Show less"): a quiet
// text row that grows the list in place.
export function ListSectionMore({
  label,
  expanded,
  onToggle,
  controls,
  variant = "sidebar",
  className,
  testId,
}: ListSectionMoreProps): React.JSX.Element {
  return (
    <li className={className}>
      <button
        aria-controls={controls}
        aria-expanded={expanded}
        className={cn(
          "flex w-full items-center gap-1.5 font-v3 text-[13px] font-medium text-v3-text-2 transition-colors hover:text-v3-text focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-v3-brand",
          variant === "card"
            ? "min-h-11 px-4 hover:bg-v3-surface-2 sm:px-5"
            : "min-h-11 rounded-[10px] px-2.5 hover:bg-[rgba(127,127,140,0.08)] lg:min-h-9",
        )}
        data-testid={testId}
        type="button"
        onClick={onToggle}
      >
        <span>{label}</span>
        <ChevronDown
          aria-hidden
          className={cn(
            "size-3.5 shrink-0 transition-transform duration-150 motion-reduce:transition-none",
            expanded && "rotate-180",
          )}
        />
      </button>
    </li>
  );
}
